/**
 * 运行轨迹抽屉 · 中栏的数据模型（PR5 新写；正文《04》§6.1，线框 01，判定卡 03 差异 3～5、9～11）。
 * 一轮的留痕事件（A-22 `tree`）→ 按层 ＋ 按事项分组：信号 → 召回 → 计划版本差异 → 事项 × N → 生成；底部按层成本。
 * 分组优先用 A-22 的 `groups[].span_ids`（PR4）；批 1 的记录没有 `groups`，按事件自己的 `plan.layer` ／ `plan.item_id` 分，结果一样。
 * 连 `plan.layer` 都没有的旧记录（分层留痕上线前）→ 平铺兜底。纯函数，不碰 React。
 */
import type { BadgeTone } from "@/components/ui/badge";
import type { PlanEvent, PlanItemView, PlanRecordDetail } from "@/lib/api/plan-lab";
import type { DemandLabel } from "../kit/demand-label";
import {
	GUARD_VERDICT_TONE,
	GUARD_VERDICT_ZH,
	GUARD_ZH,
	ITEM_EVENT_ZH,
	ITEM_STATUS_TONE,
	ITEM_STATUS_ZH,
	N2_ACTION_ZH,
	PLAN_GENERATOR_ZH,
	REPLY_TYPE_ZH,
	RESUME_ACTION_ZH,
	STOP_ZH,
	TOOL_BACKEND_ZH,
	TOOL_STATUS_ZH,
	WAIT_KIND_ZH,
	WRITE_CLASS_ZH,
	activationText,
	orderNo,
	zh,
} from "../kit/plan-labels";

export type RowKind =
	| "signal"
	| "checks"
	| "planning"
	| "activate"
	| "n2"
	| "guard"
	| "tool"
	| "stop"
	| "item_event"
	| "speakable"
	| "n3"
	| "side_check"
	| "retrieval"
	| "recall"
	| "other";

export type Status = { label: string; tone: BadgeTone };

export type TraceRow = {
	/** 事件编号；聚合行（计划校验）用 `checks` */
	id: string;
	kind: RowKind;
	/** 「层名 · 一句摘要 · 状态」的层名 */
	name: string;
	text: string;
	status?: Status;
	ms: number;
	ev?: PlanEvent;
	/** N2 一步对应的事项事件「step」（决策是否被采纳、理由） */
	step?: PlanEvent;
	/** 计划校验聚合行带的全部校验事件 */
	checks?: PlanEvent[];
};

export type GroupKind = "signal" | "recall" | "planning" | "item" | "loose" | "generation";

export type TraceGroup = {
	key: string;
	kind: GroupKind;
	title: string;
	/** 收起态摘要（展开时也显示在组头） */
	summary: string;
	status?: Status;
	/** 组头下一行置灰说明（如信号组「诉求 ／ 预取 P1 起提供」） */
	note?: string;
	rows: TraceRow[];
	/** false ＝ 整组置灰一行（召回 MVP-0；未激活事项），不展开 */
	available: boolean;
	item?: PlanItemView;
	/** 本轮刚激活（D-27） */
	activatedThisTurn?: boolean;
	/** 事项本轮用了几步（N2 次数） */
	steps?: number;
};

export type CostPart = { key: string; label: string; ms: number; tokens?: number; cost?: number };
export type CostLine = {
	totalMs: number | null;
	tokens: number | null;
	cost: number | null;
	parts: CostPart[];
};

export type TraceModel = {
	/** false ＝ 分层留痕上线前的旧记录，按节点平铺 */
	layered: boolean;
	groups: TraceGroup[];
	flat: TraceRow[];
	/** 默认展开的那组（本轮最后推进的事项）；本轮没推进任何事项为 null */
	currentKey: string | null;
	cost: CostLine;
	byId: Map<string, PlanEvent>;
	/** 本轮的计划版本（事件里的 plan.version，没有用记录行的） */
	planVersion: number | null;
};

const STRUCT = new Set(["plan.turn", "plan.layer", "plan.item"]);
/** 事项事件里不单独成行的几种（step 并进 N2 那一行；归属 ／ 提问与「问客户」重复） */
const QUIET_ITEM_EVENTS = new Set(["step", "step_attribution", "question_raised"]);

export function op(e: PlanEvent): string {
	return String(e["gen_ai.operation.name"] ?? "");
}
export function attrs(e: PlanEvent | undefined): Record<string, unknown> {
	const a = e?.attributes;
	return a && typeof a === "object" && !Array.isArray(a) ? a : {};
}
function obj(v: unknown): Record<string, unknown> {
	return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}
function str(v: unknown): string {
	return v === null || v === undefined ? "" : String(v);
}
function num(v: unknown): number {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
}

/** 字符串里的 JSON（模型原始输出）→ 对象；解析不了（含摘要截断）返回 null */
export function parseJson(raw: unknown): Record<string, unknown> | null {
	if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, unknown>;
	if (typeof raw !== "string") return null;
	try {
		const v: unknown = JSON.parse(raw);
		return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
	} catch {
		return null;
	}
}

/** 摘要截断过的模型输出（A-22 `tree=summary` 长文本只留 200 字）解析不了时，按键名抠一个字符串值 */
export function pickField(raw: unknown, key: string): string {
	if (typeof raw !== "string") return "";
	const m = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`).exec(raw);
	return m?.[1] ?? "";
}

/** 列表长度；摘要把长列表收成前 8 项 ＋「…（共 N 项）」时取 N */
export function listLen(v: unknown): number {
	if (!Array.isArray(v)) return 0;
	const last = v.at(-1);
	const m = typeof last === "string" ? /共\s*(\d+)\s*项/.exec(last) : null;
	return m ? Number(m[1]) : v.length;
}

/** 事件耗时：attributes.ms ／ latency_ms，没有就用起止时间差 */
export function eventMs(e: PlanEvent): number {
	const a = attrs(e);
	const ms = num(a.ms);
	if (ms > 0) return ms;
	const lat = num(a.latency_ms);
	if (lat > 0) return lat;
	const d = Date.parse(e.end_time) - Date.parse(e.start_time);
	return Number.isFinite(d) && d > 0 ? d : 0;
}

export function chatNode(e: PlanEvent): string {
	return str(attrs(e).node);
}

export function fmtMs(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
	if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
	if (ms > 0 && ms < 1) return "<1ms";
	return `${Math.round(ms)}ms`;
}

/** 绑定对象的一句话：订单 2003 ／ 商品 P1 ／ 无 */
export function objectText(o: unknown): string {
	const x = obj(o);
	if (x.order_id) return `订单 ${orderNo(x.order_id)}`;
	const pids = Array.isArray(x.product_ids) ? x.product_ids : [];
	if (pids.length) return `商品 ${pids.map(orderNo).join("、")}`;
	return "";
}

function itemStatus(it: PlanItemView | undefined): Status | undefined {
	if (!it) return undefined;
	if (!it.active) return { label: `未激活（${activationText(it.activation)}）`, tone: "neutral" };
	const base = zh(ITEM_STATUS_ZH, it.status);
	return {
		label: it.wait_kind ? `${base}（${zh(WAIT_KIND_ZH, it.wait_kind)}）` : base,
		tone: ITEM_STATUS_TONE[it.status] ?? "muted",
	};
}

function toolStatusTone(status: string, writeClass: string): BadgeTone {
	if (writeClass === "unknown" || writeClass === "not_written") return "danger";
	if (["error", "failed", "not_found", "rejected"].includes(status)) return "danger";
	if (["confirm_gate", "clarify", "blocked", "need_info"].includes(status)) return "warning";
	return "success";
}

/** 一个事件 → 中栏一行（「层名 · 一句摘要 · 状态」） */
export function rowOf(e: PlanEvent, next?: PlanEvent[]): TraceRow | null {
	const o = op(e);
	const a = attrs(e);
	const base = { id: e.span_id, ev: e, ms: eventMs(e) };
	if (o === "chat") {
		const node = chatNode(e);
		const parsed = parseJson(obj(a.output).raw);
		const failed = e["error.type"]
			? { label: str(e["error.type"]), tone: "danger" as const }
			: undefined;
		if (node === "n2_decide") {
			const step = next?.find(
				(x) =>
					op(x) === "plan.item_event" &&
					str(attrs(x).event_kind) === "step" &&
					x["plan.item_id"] === e["plan.item_id"],
			);
			const raw = obj(a.output).raw;
			const stepDec = obj(attrs(step).decision);
			const dec: Record<string, unknown> =
				parsed ??
				(Object.keys(stepDec).length
					? stepDec
					: {
							action: pickField(raw, "action"),
							tool_id: pickField(raw, "tool_id"),
							reason: pickField(raw, "reason"),
						});
			const act = str(dec.action);
			const what = act === "call_tool" ? str(dec.tool_id) : act === "ask" ? str(dec.slot) : "";
			const reason = str(dec.reason || dec.note || dec.why_customer);
			const text = [act ? `${zh(N2_ACTION_ZH, act)}${what ? ` ${what}` : ""}` : "决策", reason]
				.filter(Boolean)
				.join(" · ");
			const rejected = step && attrs(step).accepted === false;
			return {
				...base,
				kind: "n2",
				name: "N2",
				text,
				step,
				status: failed ?? (rejected ? { label: "未采纳", tone: "warning" } : undefined),
			};
		}
		if (node === "n3_generate") {
			const t = str(parsed?.reply_type) || pickField(obj(a.output).raw, "reply_type");
			return {
				...base,
				kind: "n3",
				name: "N3",
				text: t ? zh(REPLY_TYPE_ZH, t) : "生成回复",
				status: failed,
			};
		}
		if (node === "recall_signal") {
			const n = Array.isArray(parsed?.demands) ? parsed.demands.length : 0;
			return { ...base, kind: "other", name: "信号节点", text: `诉求 ${n} 件`, status: failed };
		}
		if (node === "recall_rank") {
			return {
				...base,
				kind: "other",
				name: "召回判定",
				text: "候选是否适配（只给建议）",
				status: failed,
			};
		}
		return { ...base, kind: "other", name: "模型", text: node || "调用", status: failed };
	}
	if (o === "plan.rank") {
		const n = a.questions && typeof a.questions === "object" ? Object.keys(a.questions).length : 0;
		return {
			...base,
			kind: "recall",
			name: "排序（Jev）",
			text: a.error
				? `调用失败：${str(a.error)}（按粗召回顺序继续）`
				: `${n} 道是非题 · ${Math.round(Number(a.ms) || 0)} 毫秒`,
			status: a.error ? { label: "降级", tone: "warning" } : undefined,
		};
	}
	if (o === "plan.recall") {
		const ds = Array.isArray(a.demands) ? (a.demands as Record<string, unknown>[]) : [];
		const n = ds.reduce((s, d) => s + (Array.isArray(d.candidates) ? d.candidates.length : 0), 0);
		return {
			...base,
			kind: "recall",
			name: "召回",
			text: a.degraded
				? `降级：${str(a.degraded)}（本轮不建事项）`
				: `诉求 ${ds.length} 件 · 候选 ${n} 个 · 只给建议，由 Agent 决定`,
			status: a.degraded || a.rejected ? { label: "没建成", tone: "warning" } : undefined,
		};
	}
	if (o === "plan.signal") {
		return { ...base, kind: "signal", name: "信号", text: signalText(a) };
	}
	if (o === "plan.planning") {
		const v = num(a.version || e["plan.version"]);
		const prev = num(a.prev_version);
		const trigger = str(a.trigger);
		const bits = [
			`${prev ? `v${prev} → ` : ""}v${v}（${zh(PLAN_GENERATOR_ZH, str(a.generator) || "rule")}）`,
			`${listLen(a.items)} 件`,
			`剔除 ${listLen(a.rejected_changes)}`,
			trigger && trigger !== "first" ? `触发 ${trigger}` : "",
		];
		return { ...base, kind: "planning", name: "计划版本", text: bits.filter(Boolean).join(" · ") };
	}
	if (o === "plan.activate") {
		return {
			...base,
			kind: "activate",
			name: "事项激活",
			text: `${str(e["plan.item_id"])} · ${activationText(obj(a.activation))}`,
		};
	}
	if (o === "plan.guard") {
		const id = str(a.guard_id);
		const v = str(a.verdict);
		const extra =
			Array.isArray(a.missing) && a.missing.length
				? `缺 ${a.missing.join("、")}`
				: str(a.why || a.swapped_to || "");
		return {
			...base,
			kind: "guard",
			name: "护栏",
			text: `${id} ${GUARD_ZH[id] ?? ""}${extra ? ` · ${extra}` : ""}`.trim(),
			status: { label: zh(GUARD_VERDICT_ZH, v), tone: GUARD_VERDICT_TONE[v] ?? "warning" },
		};
	}
	if (o === "execute_tool") {
		const status = str(a.status);
		const wc = str(a.write_class);
		const bits = [
			str(a.tool_id),
			wc ? zh(WRITE_CLASS_ZH, wc) : a.dry_run ? "预演" : "",
			zh(TOOL_BACKEND_ZH, str(a.backend) || null),
		];
		return {
			...base,
			kind: "tool",
			name: "工具",
			text: bits.filter((x) => x && x !== "—").join(" · "),
			status: status
				? { label: zh(TOOL_STATUS_ZH, status), tone: toolStatusTone(status, wc) }
				: undefined,
		};
	}
	if (o === "plan.stop") {
		const s = str(a.stop);
		const wk = str(a.wait_kind);
		return {
			...base,
			kind: "stop",
			name: "判停",
			text: `${STOP_ZH[s] ?? zh(ITEM_STATUS_ZH, s)}${wk ? `（${zh(WAIT_KIND_ZH, wk)}）` : ""}`,
			status: { label: STOP_ZH[s] ?? zh(ITEM_STATUS_ZH, s), tone: ITEM_STATUS_TONE[s] ?? "muted" },
		};
	}
	if (o === "plan.item_event") {
		const k = str(a.event_kind);
		if (QUIET_ITEM_EVENTS.has(k)) return null;
		let text = str(a.why || a.text || "");
		if (k === "state_change")
			text = `${zh(ITEM_STATUS_ZH, str(a.from))} → ${zh(ITEM_STATUS_ZH, str(a.to))}`;
		if (k === "question_asked") text = zh(WAIT_KIND_ZH, str(obj(a.pending_question).kind) || null);
		if (k === "evidence") text = "写入 1 条";
		// 同对象已提交不再提交（10-25 批 1）：复用哪件事项的那次提交
		if (k === "write_reused") text = `复用 ${str(a.reused_from) || "—"} 已提交的结果，不再提交`;
		return { ...base, kind: "item_event", name: ITEM_EVENT_ZH[k] ?? k, text: text || "—" };
	}
	if (o === "plan.speakable") {
		const items = listLen(a.items);
		return { ...base, kind: "speakable", name: "可说口径", text: `${items} 件` };
	}
	if (o === "plan.side_check") {
		const warns = sideCheckWarns(a);
		return {
			...base,
			kind: "side_check",
			name: "旁检",
			text: warns ? `告警 ${warns} 件（只告警，不重写）` : "无告警",
			status: warns ? { label: "告警", tone: "warning" } : { label: "通过", tone: "muted" },
		};
	}
	// 节点输入上限（NODE-INPUT-MAX，#635）：超了逐级截短并留痕
	if (o === "plan.input_budget") {
		const steps = Array.isArray(a.steps) ? a.steps.map(String).join("、") : "";
		return {
			...base,
			kind: "other",
			name: "输入截短",
			text: `${str(a.node).toUpperCase() || "—"} ${str(a.before)} → ${str(a.after)} 字（上限 ${str(a.limit)}）${steps ? ` · 截了 ${steps}` : ""}`,
			status: a.over ? { label: "截完仍超", tone: "warning" } : { label: "已截短", tone: "muted" },
		};
	}
	if (o === "plan.parse_retry") {
		return {
			...base,
			kind: "other",
			name: "解析重试",
			text: str(a.node) || "—",
			status: { label: "重试", tone: "warning" },
		};
	}
	if (o === "retrieval") {
		return { ...base, kind: "retrieval", name: "召回", text: str(a.summary) || "—" };
	}
	if (o === "plan.check") {
		return { ...base, kind: "checks", name: "计划校验", text: `${str(a.rule)} ${str(a.detail)}` };
	}
	return { ...base, kind: "other", name: o, text: str(e["plan.item_id"]) || "—" };
}

export function sideCheckWarns(a: Record<string, unknown>): number {
	const rs = Array.isArray(a.results) ? a.results : [];
	return rs.filter((r) => obj(r).warn).length;
}

/**
 * 接续判定的 `merged_into[]` → 可读的几段（PR6 修：原来把对象直接 String() 成「[object Object]」）。
 * 后端每项是 `{item_id, slots_merged{}, object_bound{}, old_values{}}`，或没人要的槽值 `{item_id: null, dropped{}}`；
 * 显示成「it-03（槽 order_id、reason · 绑定对象）」「未归入：size」。
 */
export function mergedList(v: unknown): string[] {
	if (!Array.isArray(v)) return [];
	return v.map((m) => {
		if (typeof m !== "object" || m === null) return str(m);
		const o = m as Record<string, unknown>;
		const keys = (x: unknown) =>
			x && typeof x === "object" && !Array.isArray(x) ? Object.keys(x as object) : [];
		if (!o.item_id) {
			const dropped = keys(o.dropped);
			return dropped.length ? `未归入：${dropped.join("、")}` : "未归入";
		}
		const slots = keys(o.slots_merged);
		const bits = [
			slots.length ? `槽 ${slots.join("、")}` : "",
			keys(o.object_bound).length ? "绑定对象" : "",
		].filter(Boolean);
		return bits.length ? `${str(o.item_id)}（${bits.join(" · ")}）` : str(o.item_id);
	});
}

function signalText(a: Record<string, unknown>): string {
	if (a.first_turn) return "首轮 · 不判接续";
	const act = str(a.resume_action);
	const target = str(a.target_item_id);
	const created = Array.isArray(a.new_item_ids) ? (a.new_item_ids as unknown[]).map(str) : [];
	const merged = mergedList(a.merged_into);
	const parts = [`接续：${zh(RESUME_ACTION_ZH, act || "none")}${target ? ` → ${target}` : ""}`];
	if (merged.length) parts.push(`合并到 ${merged.join("、")}`);
	if (created.length) parts.push(`新建 ${created.join("、")}`);
	if (a.confirm_voided) parts.push("确认作废");
	if (a.degraded) parts.push("降级");
	return parts.join(" · ");
}

/** 计划校验聚合成一行：「计划校验 V-1～V-9：通过 9」（规则全过的条数）＋ 剔除 ／ 修正几处 */
export function checksRow(checks: PlanEvent[]): TraceRow {
	const byRule = new Map<string, string[]>();
	for (const c of checks) {
		const a = attrs(c);
		const r = str(a.rule);
		byRule.set(r, [...(byRule.get(r) ?? []), str(a.verdict)]);
	}
	const rules = [...byRule.keys()].sort(
		(x, y) => num(x.replace(/\D/g, "")) - num(y.replace(/\D/g, "")),
	);
	const passRules = rules.filter((r) => (byRule.get(r) ?? []).every((v) => v === "pass")).length;
	const verdicts = checks.map((c) => str(attrs(c).verdict));
	const rejected = verdicts.filter((v) => v === "reject").length;
	const fixed = verdicts.filter((v) => v !== "pass" && v !== "reject").length;
	const range = rules.length > 1 ? `${rules[0]}～${rules[rules.length - 1]}` : (rules[0] ?? "");
	const tail = [rejected ? `剔除 ${rejected}` : "", fixed ? `修正 ${fixed}` : ""].filter(Boolean);
	return {
		id: "checks",
		kind: "checks",
		name: "计划校验",
		text: `${range}：通过 ${passRules}${tail.length ? ` · ${tail.join(" · ")}` : ""}`,
		status: tail.length
			? { label: "有剔除 ／ 修正", tone: "warning" }
			: { label: "全过", tone: "success" },
		ms: 0,
		checks,
	};
}

/** 留痕层名（S12 起＝七环节）→ 抽屉里的分组桶；旧记录的层名原样 */
const LINK_BUCKET: Record<string, string> = {
	board: "planning",
	decide: "execution",
	execute: "execution",
	settle: "execution",
	reply: "generation",
};
const bucketOf = (layer: unknown) => LINK_BUCKET[str(layer)] ?? str(layer);

function groupKeyOf(e: PlanEvent): string | null {
	const o = op(e);
	if (STRUCT.has(o)) return null;
	const layer = bucketOf(e["plan.layer"]);
	if (o === "retrieval" || layer === "recall") return "recall";
	if (layer === "signal") return "signal";
	if (layer === "planning") return "planning";
	if (layer === "generation") return "generation";
	if (layer === "execution") return e["plan.item_id"] ? `item:${e["plan.item_id"]}` : "execution";
	return null;
}

/** A-22 `groups`（PR4）→ 事件归哪组；没有就按字段分 */
function membership(d: PlanRecordDetail): (e: PlanEvent) => string | null {
	if (!d.groups?.length) return groupKeyOf;
	const map = new Map<string, string>();
	for (const g of d.groups) {
		const key = g.item_id
			? `item:${g.item_id}`
			: g.key && g.key !== "cost"
				? bucketOf(g.key)
				: g.layer === "cost"
					? ""
					: bucketOf(g.layer);
		if (!key) continue;
		for (const id of g.span_ids ?? []) if (!map.has(id)) map.set(id, key);
	}
	return (e) => map.get(e.span_id) ?? groupKeyOf(e);
}

function rowsOf(evs: PlanEvent[]): TraceRow[] {
	const out: TraceRow[] = [];
	evs.forEach((e, i) => {
		const r = rowOf(e, evs.slice(i + 1));
		if (r) out.push(r);
	});
	return out;
}

export function buildTraceModel(d: PlanRecordDetail, demandLabel: DemandLabel): TraceModel {
	const tree = d.tree ?? [];
	const byId = new Map(tree.map((e) => [e.span_id, e]));
	const layered = !!d.groups?.length || tree.some((e) => !!e["plan.layer"]);
	const versions = tree
		.map((e) => e["plan.version"])
		.filter((v): v is number => typeof v === "number");
	const planVersion = versions.length ? Math.max(...versions) : (d.record.plan_version ?? null);
	const cost = costLine(d);
	if (!layered) {
		return {
			layered,
			groups: [],
			flat: rowsOf(tree.filter((e) => !STRUCT.has(op(e)))),
			currentKey: null,
			cost,
			byId,
			planVersion,
		};
	}
	const keyOf = membership(d);
	const buckets = new Map<string, PlanEvent[]>();
	for (const e of tree) {
		const k = keyOf(e);
		if (!k) continue;
		buckets.set(k, [...(buckets.get(k) ?? []), e]);
	}
	const groups: TraceGroup[] = [];

	// 1 信号（MVP-0：接续判定；诉求 ／ 置信档位 ／ 预取 P1 起提供）
	const sig = buckets.get("signal") ?? [];
	const sigEv = sig.find((e) => op(e) === "plan.signal");
	groups.push({
		key: "signal",
		kind: "signal",
		title: "信号",
		summary: sigEv ? signalText(attrs(sigEv)) : sig.length ? "接续判定" : "本轮没有接续判定",
		note: "诉求 ／ 置信档位 ／ 预取：P1 起提供",
		rows: rowsOf(sig),
		available: true,
	});

	// 2 召回（规则计划器在决策点跑；非决策点没有召回）
	const rec = buckets.get("recall") ?? [];
	const recRow = rowsOf(rec).find((r) => r.kind === "recall");
	groups.push({
		key: "recall",
		kind: "recall",
		title: "召回",
		summary: rec.length ? (recRow?.text ?? "召回") : "本轮没有召回（不是决策点）",
		rows: rowsOf(rec),
		available: rec.length > 0,
	});

	// 3 计划版本差异
	const plan = buckets.get("planning") ?? [];
	const checks = plan.filter((e) => op(e) === "plan.check");
	const planRows: TraceRow[] = [];
	let checksAdded = false;
	plan.forEach((e, i) => {
		if (op(e) === "plan.check") {
			if (!checksAdded) planRows.push(checksRow(checks));
			checksAdded = true;
			return;
		}
		const r = rowOf(e, plan.slice(i + 1));
		if (r) planRows.push(r);
	});
	const planning = plan.filter((e) => op(e) === "plan.planning").at(-1);
	const activated = plan.filter((e) => op(e) === "plan.activate");
	let planSummary: string;
	if (planning) {
		planSummary = rowOf(planning)?.text ?? `v${planVersion ?? "?"}`;
	} else {
		planSummary = planVersion ? `v${planVersion} 未变` : "本轮没有计划";
	}
	const lateActivated = activated.filter((e) => str(attrs(e).activated_by) !== "first_turn");
	if (lateActivated.length)
		planSummary += ` · 激活 ${lateActivated.map((e) => str(e["plan.item_id"])).join("、")}`;
	groups.push({
		key: "planning",
		kind: "planning",
		title: "计划",
		summary: planSummary,
		rows: planRows,
		available: true,
	});

	// 4 事项 × N（按本轮末事项清单的顺序；本轮刚激活的组头标「本轮激活」；未激活的灰显一行）
	const itemIds: string[] = d.items_now.map((i) => i.item_id);
	for (const k of buckets.keys())
		if (k.startsWith("item:") && !itemIds.includes(k.slice(5))) itemIds.push(k.slice(5));
	const lateIds = new Set(lateActivated.map((e) => str(e["plan.item_id"])));
	let currentKey: string | null = null;
	let lastAdvance = -1;
	const order = new Map(tree.map((e, i) => [e.span_id, i]));
	for (const id of itemIds) {
		const key = `item:${id}`;
		const evs = buckets.get(key) ?? [];
		const it = d.items_now.find((x) => x.item_id === id);
		const rows = rowsOf(evs);
		const steps = rows.filter((r) => r.kind === "n2").length;
		const st = itemStatus(it);
		const head = [id, it ? demandLabel(it.demand_type) : "", objectText(it?.object)]
			.filter(Boolean)
			.join(" ");
		const inactive = !!it && !it.active;
		let summary: string;
		if (inactive) summary = `${head} · ${st?.label ?? "未激活"}`;
		else if (!evs.length) summary = `${head} · 本轮未推进`;
		else summary = `${head}（${steps} 步）`;
		for (const e of evs)
			if (["chat", "execute_tool", "plan.stop"].includes(op(e))) {
				const idx = order.get(e.span_id) ?? -1;
				if (idx > lastAdvance) {
					lastAdvance = idx;
					currentKey = key;
				}
			}
		groups.push({
			key,
			kind: "item",
			title: id,
			summary,
			status: st,
			rows,
			available: !inactive && evs.length > 0,
			item: it,
			activatedThisTurn: lateIds.has(id),
			steps,
		});
	}
	const loose = buckets.get("execution") ?? [];
	if (loose.length)
		groups.push({
			key: "execution",
			kind: "loose",
			title: "执行（没归到事项）",
			summary: `${rowsOf(loose).length} 步`,
			rows: rowsOf(loose),
			available: true,
		});

	// 5 生成
	const gen = buckets.get("generation") ?? [];
	const genRows = rowsOf(gen);
	const n3 = genRows.find((r) => r.kind === "n3");
	const side = gen.find((e) => op(e) === "plan.side_check");
	const called = tree.filter((e) => op(e) === "execute_tool").length;
	const warns = side ? sideCheckWarns(attrs(side)) : 0;
	const used = d.used_in_answer?.available
		? "用进答案见详情"
		: `用进答案 ${d.used_in_answer?.note ?? "P1 起提供"}`;
	groups.push({
		key: "generation",
		kind: "generation",
		title: "生成",
		summary: n3
			? [`N3 · ${n3.text}`, `调用了 ${called}`, used, warns ? `旁检告警 ${warns}` : ""]
					.filter(Boolean)
					.join(" · ")
			: "未生成",
		rows: genRows,
		available: true,
		status: warns ? { label: "旁检告警", tone: "warning" } : undefined,
	});

	return { layered, groups, flat: [], currentKey, cost, byId, planVersion };
}

/** 底部成本：本轮总耗时 ／ token ／ 花费 ＋ 按层拆（MVP-0：接续判定 ／ N2 ／ 工具 ／ 护栏 ／ N3；P1 起加信号、排序、计划器） */
export function costLine(d: PlanRecordDetail): CostLine {
	const tree = d.tree ?? [];
	const r = d.record;
	const chats = tree.filter((e) => op(e) === "chat");
	const chatMs = (pred: (e: PlanEvent) => boolean) =>
		chats.filter(pred).reduce((s, e) => s + eventMs(e), 0);
	const chatTok = (pred: (e: PlanEvent) => boolean) =>
		chats
			.filter(pred)
			.reduce(
				(s, e) => s + num(e["gen_ai.usage.input_tokens"]) + num(e["gen_ai.usage.output_tokens"]),
				0,
			);
	const chatCost = (pred: (e: PlanEvent) => boolean) =>
		chats.filter(pred).reduce((s, e) => s + num(e["plan.cost_usd"]), 0);
	const layerCost = new Map((d.cost_by_layer ?? []).map((c) => [bucketOf(c.layer), c]));
	const llm = (
		key: string,
		label: string,
		layer: string,
		pred: (e: PlanEvent) => boolean,
	): CostPart | null => {
		const c = layerCost.get(layer);
		const ms = c?.latency_ms ?? chatMs(pred);
		const tokens = c ? num(c.input_tokens) + num(c.output_tokens) : chatTok(pred);
		const cost = c?.cost_usd ?? chatCost(pred);
		if (!ms && !tokens && !chats.some(pred)) return null;
		return { key, label, ms, tokens, cost };
	};
	const parts: CostPart[] = [];
	const sig = llm("signal", "信号", "signal", (e) => bucketOf(e["plan.layer"]) === "signal");
	if (sig) parts.push(sig);
	for (const [layer, label] of [
		["recall", "排序"],
		["planning", "计划器"],
	] as const) {
		const p = llm(layer, label, layer, (e) => bucketOf(e["plan.layer"]) === layer);
		if (p) parts.push(p);
	}
	const n2 = llm("n2", "N2", "execution", (e) => bucketOf(e["plan.layer"]) === "execution");
	if (n2) parts.push(n2);
	const toolMs = tree.filter((e) => op(e) === "execute_tool").reduce((s, e) => s + eventMs(e), 0);
	if (tree.some((e) => op(e) === "execute_tool"))
		parts.push({ key: "tool", label: "工具", ms: toolMs });
	const guardMs = tree.filter((e) => op(e) === "plan.guard").reduce((s, e) => s + eventMs(e), 0);
	if (tree.some((e) => op(e) === "plan.guard"))
		parts.push({ key: "guard", label: "护栏", ms: guardMs });
	const n3 = llm("n3", "N3", "generation", (e) => bucketOf(e["plan.layer"]) === "generation");
	if (n3) parts.push(n3);
	const tokens = r ? num(r.tokens_in) + num(r.tokens_out) : null;
	return {
		totalMs: r ? num(r.latency_ms) : null,
		tokens,
		cost: r ? num(r.cost_usd) : null,
		parts,
	};
}
